'use server'

import { apiRequest, ApiResponse } from '../request'

export type DicomModality =
    | 'CR'
    | 'CT'
    | 'MR'
    | 'US'
    | 'XA'
    | 'RF'
    | 'DX'
    | 'MG'
    | 'NM'
    | 'PT'
    | 'OT'

export interface PacsServer {
    id: number
    companyId: number
    name: string
    aeTitle: string
    host: string
    port: number
    active: boolean
    wadoRsUrl?: string
    qidoRsUrl?: string
    stowRsUrl?: string
    viewerUrl?: string
    authType?: string
    authCredentials?: string
    settings?: any
    createdAt: string
    updatedAt: string
    _count?: {
        studies: number
    }
}

export interface DicomStudy {
    id: number
    serverId: number
    server?: {
        id: number
        name: string
    }
    studyInstanceUid: string
    patientId?: string
    patientName?: string
    patientBirthDate?: string
    studyDate?: string
    studyTime?: string
    studyDescription?: string
    accessionNumber?: string
    modality?: string
    numberOfSeries?: number
    numberOfInstances?: number
    clientId?: number
    client?: {
        id: number
        name: string
    }
    examId?: number
    exam?: {
        id: number
        title: string
    }
    metadata?: any
    createdAt: string
    updatedAt: string
    series?: DicomSeries[]
}

export interface DicomSeries {
    id: number
    studyId: number
    seriesInstanceUid: string
    seriesNumber?: number
    modality?: DicomModality
    seriesDescription?: string
    bodyPartExamined?: string
    numberOfInstances?: number
    metadata?: any
    createdAt: string
    updatedAt: string
}

export interface CreatePacsServerData {
    name: string
    aeTitle: string
    host: string
    port: number
    active?: boolean
    wadoRsUrl?: string
    qidoRsUrl?: string
    stowRsUrl?: string
    viewerUrl?: string
    authType?: string
    authCredentials?: string
    settings?: any
}

export interface UpdatePacsServerData extends Partial<CreatePacsServerData> {}

export interface QueryStudiesParams {
    patientName?: string
    patientId?: string
    studyDate?: string
    modality?: string
    accessionNumber?: string
}

export interface LinkStudyData {
    clientId?: number
    examId?: number
}

export interface PacsStats {
    totalServers: number
    activeServers: number
    totalStudies: number
    studiesLinked: number
    byModality: Record<string, number>
}

// Server CRUD
export async function getPacsServers(): Promise<ApiResponse<PacsServer[]>> {
    return apiRequest<PacsServer[]>('pacs/servers', {})
}

export async function getPacsServersByCompany(
    companyId: number
): Promise<ApiResponse<PacsServer[]>> {
    return apiRequest<PacsServer[]>(
        `pacs/servers/company/${companyId}`,
        {}
    )
}

export async function getPacsServer(
    id: number
): Promise<ApiResponse<PacsServer>> {
    return apiRequest<PacsServer>(`pacs/servers/${id}`, {})
}

export async function createPacsServer(
    data: CreatePacsServerData
): Promise<ApiResponse<PacsServer>> {
    return apiRequest<PacsServer>('pacs/servers', { data }, 'post')
}

export async function updatePacsServer(
    id: number,
    data: UpdatePacsServerData
): Promise<ApiResponse<PacsServer>> {
    return apiRequest<PacsServer>(`pacs/servers/${id}`, { data }, 'put')
}

export async function deletePacsServer(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`pacs/servers/${id}`, {}, 'delete')
}

export async function testPacsServer(
    id: number
): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return apiRequest<{ success: boolean; message: string }>(
        `pacs/servers/${id}/test`,
        {},
        'post'
    )
}

// Studies
export async function getDicomStudies(
    serverId?: number
): Promise<ApiResponse<DicomStudy[]>> {
    const params = serverId ? { serverId } : {}
    return apiRequest<DicomStudy[]>('pacs/studies', { params })
}

export async function getDicomStudy(
    id: number
): Promise<ApiResponse<DicomStudy>> {
    return apiRequest<DicomStudy>(`pacs/studies/${id}`, {})
}

export async function queryStudies(
    serverId: number,
    params: QueryStudiesParams
): Promise<ApiResponse<DicomStudy[]>> {
    return apiRequest<DicomStudy[]>(`pacs/servers/${serverId}/query`, {
        params,
    })
}

export async function linkStudy(
    studyId: number,
    data: LinkStudyData
): Promise<ApiResponse<DicomStudy>> {
    return apiRequest<DicomStudy>(`pacs/studies/${studyId}/link`, { data }, 'post')
}

export async function unlinkStudy(
    studyId: number
): Promise<ApiResponse<DicomStudy>> {
    return apiRequest<DicomStudy>(`pacs/studies/${studyId}/unlink`, {}, 'post')
}

export async function getStudyViewerUrl(
    studyId: number
): Promise<ApiResponse<{ url: string }>> {
    return apiRequest<{ url: string }>(`pacs/studies/${studyId}/viewer-url`, {})
}

export async function getStudiesByClient(
    clientId: number
): Promise<ApiResponse<DicomStudy[]>> {
    return apiRequest<DicomStudy[]>(`pacs/client/${clientId}/studies`, {})
}

export async function getStudiesByExam(
    examId: number
): Promise<ApiResponse<DicomStudy[]>> {
    return apiRequest<DicomStudy[]>(`pacs/exam/${examId}/studies`, {})
}

export async function getPacsStats(): Promise<ApiResponse<PacsStats>> {
    return apiRequest<PacsStats>('pacs/stats', {})
}

export const DICOM_MODALITY_LABELS: Record<DicomModality, string> = {
    CR: 'Radiografia Computadorizada',
    CT: 'Tomografia Computadorizada',
    MR: 'Ressonancia Magnetica',
    US: 'Ultrassonografia',
    XA: 'Angiografia',
    RF: 'Fluoroscopia',
    DX: 'Radiografia Digital',
    MG: 'Mamografia',
    NM: 'Medicina Nuclear',
    PT: 'PET Scan',
    OT: 'Outros',
}

export const DICOM_MODALITY_SHORT_LABELS: Record<DicomModality, string> = {
    CR: 'CR',
    CT: 'TC',
    MR: 'RM',
    US: 'US',
    XA: 'XA',
    RF: 'RF',
    DX: 'RX',
    MG: 'MG',
    NM: 'NM',
    PT: 'PET',
    OT: 'OT',
}
